import { Injectable, OnDestroy } from "@angular/core";
import { Observable, BehaviorSubject, of, Subscription } from "rxjs";
import { map, catchError, switchMap, finalize, shareReplay } from "rxjs/operators";
import { UserModel } from "../_models/user.model";
import { AuthModel } from "../_models/auth.model";
import { AuthHTTPService } from "./auth-http";
import { environment } from "src/environments/environment";
import { Router } from "@angular/router";
import { AuthenticatedResponse } from "src/app/shared/models/AuthenticatedResponse";
import { Identifiers } from "src/app/shared/models/Identifiers";
import { AuthenticationRequest } from "src/app/shared/models/AuthenticationRequest";
import { AuthConfigService } from "./auth-config.service";
import { HttpClient } from "@angular/common/http";
import { TokenStorageService } from "src/app/services/token-storage.service";
import { Response } from "src/app/shared/models/Response";
import { MsalUserProfile } from "src/app/shared/models/MsalUserProfile";
import { MsalCredentials } from "src/app/shared/models/MsalCredentials";
import { PermissionService } from "src/app/services/permission.service";
import { LocalStorageService } from "src/app/services/local-storage.service";
import { SocialOAuthProvider } from "../_models/social-oauth-provider.enum";
import {
	CompleteOAuthRegistrationRequest,
	RegisterUserRequest,
} from "../_models/oauth-registration.model";
@Injectable({
	providedIn: "root",
})
export class AuthService implements OnDestroy {
	// private fields
	private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
	private authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;
	private pendingAuthenticatedResponse: AuthenticatedResponse | null = null;
	private logoutRequest$: Observable<any> | null = null;

	// public fields
	currentUser$: Observable<UserModel>;
	isLoading$: Observable<boolean>;
	currentUserSubject: BehaviorSubject<UserModel>;
	isLoadingSubject: BehaviorSubject<boolean>;
	isLoadingRecoverPassword$: Observable<boolean>;
	isLoadingRecoverPasswordSubject: BehaviorSubject<boolean>;

	get currentUserValue(): UserModel {
		return this.currentUserSubject.value;
	}

	set currentUserValue(user: UserModel) {
		this.currentUserSubject.next(user);
	}

	constructor(private authHttpService: AuthHTTPService, private config: AuthConfigService,
		private localStorage: LocalStorageService,
		private tokenStorage: TokenStorageService, private http: HttpClient, private router: Router, private permissionStorage: PermissionService) {
		this.isLoadingSubject = new BehaviorSubject<boolean>(false);
		this.currentUserSubject = new BehaviorSubject<UserModel>(undefined);
		this.currentUser$ = this.currentUserSubject.asObservable();
		this.isLoading$ = this.isLoadingSubject.asObservable();
		this.isLoadingRecoverPasswordSubject = new BehaviorSubject<boolean>(false);
		this.isLoadingRecoverPassword$ = this.isLoadingRecoverPasswordSubject.asObservable();
		//const subscr = this.getUserByToken().subscribe();
		//this.unsubscribe.push(subscr);
	}

	linkAccessMethod(code: string, provider: SocialOAuthProvider): Observable<Response<boolean>> {
		this.isLoadingSubject.next(true);
		return this.http
			.post<Response<boolean>>(`${this.config.wepApiRoute}/MiCuenta/metodos-acceso/link`, { code, provider })
			.pipe(finalize(() => this.isLoadingSubject.next(false)));
	}

	tokenLogin(email: string, password: string): Observable<AuthenticatedResponse> {
		this.isLoadingSubject.next(true);
		let identifier: Identifiers = new Identifiers();
		identifier.email = email;
		let loginRequest: AuthenticationRequest = new AuthenticationRequest(identifier, password);
		return this.http.post<AuthenticatedResponse>(this.config.wepApiRoute + this.config.loginEndpoint, loginRequest).pipe(finalize(() => this.isLoadingSubject.next(false)));
	}

	oauthLogin(code: string, provider: SocialOAuthProvider): Observable<AuthenticatedResponse> {
		this.isLoadingSubject.next(true);
		return this.http
			.post<AuthenticatedResponse>(this.config.wepApiRoute + this.config.oauthLoginEndpoint, { code, provider })
			.pipe(finalize(() => this.isLoadingSubject.next(false)));
	}

	register(request: RegisterUserRequest): Observable<Response<any>> {
		this.isLoadingSubject.next(true);
		return this.http
			.post<Response<any>>(this.config.wepApiRoute + this.config.registerEndpoint, request)
			.pipe(finalize(() => this.isLoadingSubject.next(false)));
	}

	completeOAuthRegistration(request: CompleteOAuthRegistrationRequest): Observable<AuthenticatedResponse> {
		this.isLoadingSubject.next(true);
		return this.http
			.post<AuthenticatedResponse>(this.config.wepApiRoute + this.config.oauthRegisterEndpoint, request)
			.pipe(finalize(() => this.isLoadingSubject.next(false)));
	}

	setPendingAuthenticatedResponse(response: AuthenticatedResponse) {
		this.pendingAuthenticatedResponse = response;
	}

	consumePendingAuthenticatedResponse(): AuthenticatedResponse | null {
		const response = this.pendingAuthenticatedResponse;
		this.pendingAuthenticatedResponse = null;
		return response;
	}

	activeDirectoryLogin(userProfile: MsalUserProfile, email: string, token: string): Observable<AuthenticatedResponse> {
		let credentials = new MsalCredentials();
		credentials.email = email;
		credentials.token = token;
		credentials.name = userProfile.givenName;
		credentials.surname = userProfile.surname;
		credentials.displayName = userProfile.displayName; // example: "EIG  Atacama Management SpA"
		return this.http.post<AuthenticatedResponse>(this.config.wepApiRoute + this.config.activeDirectoryEndpoint, credentials); //ruta nueva
		//return this.http.post<AuthenticatedResponse>(this.config.wepApiRoute + this.config.activeDirectoryEndpoint+"?email="+email+"&token="+token,null)
	}

	tokenList(): Observable<any> {
		return this.http.get<any>(this.config.wepApiRoute + "/authentication/list");
	}

	recoverPassword(email: string, rut: string): Observable<Response<any>> {
		this.isLoadingRecoverPasswordSubject.next(true);
		return this.http.post<any>(this.config.wepApiRoute + this.config.recoverPassEndpoint + email + "&rut=" + rut, null).pipe(finalize(() => this.isLoadingRecoverPasswordSubject.next(false)));
	}
	changePass(usuarioId: string, currentPass: string | null, newPass: string): Observable<any> {
		let formData: FormData = new FormData();
		formData.append("usuarioId", usuarioId);
		if (currentPass) {
			formData.append("currentPass", currentPass);
		}
		formData.append("newPass", newPass);
		return this.http.post<any>(this.config.wepApiRoute + "/authentication/changePassword", formData);
	}
	changeTempPassword(usuarioId: string, newPassword: string, tempPassword: string): Observable<Response<string>> {
		let formData: FormData = new FormData();
		formData.append("usuarioId", usuarioId);
		formData.append("nuevoPassword", newPassword);
		formData.append("passwordTemporal", tempPassword);
		this.isLoadingSubject.next(true);
		return this.http.post<Response<string>>(this.config.wepApiRoute + this.config.changePasswordEndpoint, formData).pipe(finalize(() => this.isLoadingSubject.next(false)));
	}

	blockAccount(email: string, ipAddress): Observable<any> {
		let formData: FormData = new FormData();
		formData.append("email", email);
		formData.append("ipAddress", ipAddress);
		return this.http.post<any>(this.config.wepApiRoute + this.config.blockAccountEndpoint, formData);
	}

	getIPAddress() {
		return this.http.get("http://api.ipify.org/?format=json");
	}

	//logout() {
	//	this.tokenStorage.signOut();
	//	this.localStorage.signOut();
	//	this.permissionStorage.signOut();
	//	this.router.navigate(["/auth/login"], {
	//		queryParams: {},
	//	});
	//}
	logout() {
		if (this.logoutRequest$) return this.logoutRequest$;
		const session = this.localStorage.getUserSession();
		const logoutRequest = { SessionId: session ? session.id : null };
	  
		const request$ = session?.id ? this.http.post(
		  `${this.config.wepApiRoute}/authentication/logout`, 
		  logoutRequest
		) : of(null);
		this.logoutRequest$ = request$.pipe(
		  finalize(() => {
			this.tokenStorage.signOut();
			this.localStorage.signOut();
			this.permissionStorage.signOut();
			this.router.navigate(["/auth/login"], { queryParams: {} });
			this.logoutRequest$ = null;
		  }),
		  shareReplay({ bufferSize: 1, refCount: false })
		);
		return this.logoutRequest$;
	  }
	  
	  
	  manualLogout(userId: string): Observable<Response<boolean>> {
		return this.http.post<Response<boolean>>(
		  `${this.config.wepApiRoute}/authentication/manualLogout/${userId}`, {}
		);
	  }

	verificarSesion(usuarioId: string, sesionId: string): Observable<any> {
		return this.http.get<any>(`${this.config.wepApiRoute}/authentication/verificarSesion?usuarioId=${usuarioId}&sesionId=${sesionId}`);
	}


	getUserByToken(): Observable<UserModel> {
		const auth = this.getAuthFromLocalStorage();
		if (!auth || !auth.authToken) {
			return of(undefined);
		}

		this.isLoadingSubject.next(true);
		return this.authHttpService.getUserByToken(auth.authToken).pipe(
			map((user: UserModel) => {
				if (user) {
					this.currentUserSubject = new BehaviorSubject<UserModel>(user);
				} else {
					this.logout().subscribe();
				}
				return user;
			}),
			finalize(() => this.isLoadingSubject.next(false))
		);
	}

	forgotPassword(email: string): Observable<boolean> {
		this.isLoadingSubject.next(true);
		return this.http.post<any>(this.config.wepApiRoute + this.config.recoverPassEndpoint + email, null).pipe(finalize(() => this.isLoadingSubject.next(false)));
	}

	private getAuthFromLocalStorage(): AuthModel {
		try {
			const authData = JSON.parse(localStorage.getItem(this.authLocalStorageToken));
			return authData;
		} catch (error) {
			console.error(error);
			return undefined;
		}
	}

	ngOnDestroy() {
		this.unsubscribe.forEach((sb) => sb.unsubscribe());
	}
	forceLogout(email: string, rut: string) {
		return this.http.post<Response<boolean>>(
			`${this.config.wepApiRoute}/authentication/forceLogout`,
			{ email, rut }
		);
	}



}
